import {
  useAddCheckoutPromoCode,
  useRemoveCheckoutPromoCode,
} from 'src/react_query/queries/hooks'
import { CheckoutFragment } from 'src/saleor_graphql/generated/graphql'
import usePersistedUserData from './usePersistedUserData'
import { parseCheckout } from 'src/react_query/utils'

const useUpdateCheckoutVouchers = () => {
  const { account } = usePersistedUserData()
  const {
    mutate: addCheckoutPromoCode,
    isLoading: isAddLoading,
    isError: isAddError,
  } = useAddCheckoutPromoCode()
  const {
    mutate: removeCheckoutPromoCode,
    isLoading: isRemoveLoading,
    isError: isRemoveError,
  } = useRemoveCheckoutPromoCode()

  const updateCheckoutVouchers = (checkout: CheckoutFragment) => {
    const affiliateFreeVoucherCode = 'affiliatediscount'
    const freeAffiliateProductTypeId = 'UHJvZHVjdFR5cGU6NzA='
    const freeSubscriptionProductTypeId = 'UHJvZHVjdFR5cGU6NDQ='

    const parsedCheckout = parseCheckout(checkout)

    const hasFullAccessFreeAffiliate =
      account?.hasFullAccessFreeAffiliate ||
      parsedCheckout.lines.some(line => {
        return line.variant.gives90DayFreeAffiliate?.toLowerCase() === 'true'
      })

    const lineVoucherCodes = Array.from(
      new Set(
        parsedCheckout.lines
          .map(line => {
            return line.variant.voucherCode?.toLowerCase() ?? ''
          })
          .filter(voucher => voucher !== '')
          .concat(hasFullAccessFreeAffiliate ? [affiliateFreeVoucherCode] : []),
      ),
    )

    const suggestedAddons = parsedCheckout.lines
      .map(line => ({
        promo: line.variant.parsedAttributes.suggestedAddonPromo,
        addon: line.variant.parsedAttributes.suggestedAddon,
      }))
      .filter(a => Boolean(a.addon))

    const checkoutLineVariants = parsedCheckout.lines.map(
      line => line.variant.id,
    )
    const accountType = account ? account.type : 'NON_AUTH'
    const suggestedAddonPromosToApply = suggestedAddons
      .filter(a => {
        const { addon, promo } = a

        const isSuggestedProductInCart = addon?.reference
          ? checkoutLineVariants.includes(addon?.reference)
          : false
        const isValidUserType = promo.forUserTypes.includes(accountType)

        return isSuggestedProductInCart && isValidUserType
      })
      .map(addon => addon.promo.code)
      .filter((c): c is string => Boolean(c))

    const shouldHaveVoucher =
      (lineVoucherCodes.length > 0 &&
        parsedCheckout.lines.some(line => {
          return [
            freeAffiliateProductTypeId,
            freeSubscriptionProductTypeId,
          ].some(
            productTypeId =>
              line.variant.product.productType.id === productTypeId,
          )
        })) ||
      suggestedAddonPromosToApply.length > 0

    if (!parsedCheckout.voucherCode && shouldHaveVoucher) {
      addCheckoutPromoCode({
        id: parsedCheckout.id,
        promoCode: lineVoucherCodes[0] || suggestedAddonPromosToApply[0],
      })
    }
    if (parsedCheckout.voucherCode && !shouldHaveVoucher) {
      removeCheckoutPromoCode({
        id: parsedCheckout.id,
        promoCode: parsedCheckout.voucherCode,
      })
    }
  }

  return {
    updateCheckoutVouchers,
    isLoading: isAddLoading || isRemoveLoading,
    isError: isAddError || isRemoveError,
  }
}

export default useUpdateCheckoutVouchers
